import { FC, useState, useEffect } from 'react';
import styled from './index.module.scss';
import NextImage from '@/components/NextImage';
import { Button, message } from '@/components/UI';
import { useGlobal } from '@/utils/useGlobal';
import { getProductList } from '@/service/referral';
import type { IProductItem } from '@/components/Referral/index.d';
import { formatPrice } from '@/components/Referral/util';
import { useReferralStore } from '@/components/Referral/ReferralProvider';
import gtm from '@/utils/gtm';

const Products = ({ type }: { type?: string }) => {
    const { useLang, useRouter, Link } = useGlobal();
    const { locale } = useRouter();
    const { actStatus, pageUsing } = useReferralStore();
    const { t } = useLang('referral');
    const [data, setData] = useState<IProductItem[]>([]);

    useEffect(() => {
        if (actStatus !== null && ['us', 'ca'].includes(locale)) {
            // 后端改了新规则，活动前沿用之前状态，活动中和结束后传不同参数获取product
            const getProductParams = (locales: string[], actStatus: number | null) => {
                const _default = {
                    bizModule: 'REFERRAL',
                    module: 'HOT',
                };
                const _active = {
                    bizModule: 'REFERRAL',
                    module: 'ACTIVITY_PROGRESS',
                };
                const _end = { bizModule: 'REFERRAL', module: 'ACTIVITY_END' };
                // New rules was limited in NA exclude dp3;
                if (type && type !== 'dp3' && locales.includes(locale)) {
                    if (actStatus === 1) return _active;
                    if (actStatus === 0) return _end;
                }

                if (type && type === 'dp3') {
                    return {
                        bizModule: 'DP3_REFERRAL',
                        module: 'NORMAL',
                    };
                }
                return _default;
            };

            const params = getProductParams(['us', 'ca'], actStatus) as {
                bizModule: 'REFERRAL';
                module: 'NORMAL' | 'HOT' | 'ACTIVITY_PROGRESS' | 'ACTIVITY_END';
            };

            getProductList(params, locale).then((res) => {
                if (res.code !== '0') return message.error(res.message);
                let newData = res.data;
                if (type === 'dp3') {
                    newData = res.data.map((item) => ({ ...item, productUrl: `https://${locale}.ecoflow.com/cart/${item.variantId}:1?channel=buy_button` }));
                }

                setData(newData);
            });
        }

        // 先放出来，如果是dp3或者其它活动再另外处理
        if (!['us', 'ca'].includes(locale)) {
            let params = {
                bizModule: 'REFERRAL',
                module: 'HOT',
            };
            if (pageUsing === 'd3ms') {
                switch (locale) {
                    case 'uk':
                        params = { bizModule: 'D3M_REFERRAL_20251216', module: 'CAMPAIGN_20251216' };
                        break;
                    default:
                        params = { bizModule: 'DP3_REFERRAL_20251020', module: 'CAMPAIGN_20251020' };
                }
            }
            getProductList(params, locale).then((res) => {
                if (res.code !== '0') return message.error(res.message);
                const newData = res.data;
                newData.forEach((item) => {
                    if (parseInt(item.compareAtPrice) > parseInt(item.price)) {
                        item.discount = `${Math.round((1 - parseInt(item.price) / parseInt(item.compareAtPrice)) * 100)}%`;
                    } else {
                        item.discount = '';
                    }
                });

                setData(newData);
            });
        }
    }, [actStatus]);

    if (!data || data.length === 0) return <></>;

    return (
        <div className={`${styled.products}`}>
            <h3 className="title">{actStatus === 1 ? t('products.title_1') : t('products.title')}</h3>
            <p className="desc">{actStatus === 1 ? t('products.desc_1') : t('products.desc')}</p>
            <div className={`products_content ${type === 'dp3' ? 'column4' : ''}`}>
                {data &&
                    data.map((item, index) => (
                        <div key={index} className="product_item">
                            {item.discount &&
                                (['fr'].includes(locale) ? (
                                    <div className="discount">
                                        {t('off')}&nbsp;{item.discount}
                                    </div>
                                ) : (
                                    <div className="discount">
                                        {item.discount}&nbsp;{t('off')}
                                    </div>
                                ))}
                            <div>
                                <NextImage className="img" src={item.imageUrl} alt={item.title} aria-label={item.title} />
                                <p className="name">{item.title}</p>
                                <p className="subname">{item.subtitle1}</p>
                                <p className="subname">{item.subtitle2}</p>
                            </div>
                            <div>
                                <p className="pice">
                                    {`${formatPrice(locale, parseFloat(item.price))} `}
                                    {item.compareAtPrice && parseInt(item.compareAtPrice) > parseInt(item.price) && (
                                        <span className="underline">
                                            {formatPrice(locale, parseFloat(item.compareAtPrice))}
                                            {/* {i18nCCYMap[locale]}
                                    {item.compareAtPrice} */}
                                        </span>
                                    )}
                                </p>
                                <Link href={item.productUrl} traget="_blank">
                                    <Button
                                        className="refer-btn"
                                        onClick={() => {
                                            try {
                                                gtm.push({
                                                    event: 'uaEvent',
                                                    event_name: 'buy',
                                                    product_name: item.title,
                                                    position: `${t('products.title')}_${t('products.buy_now')}`,
                                                });
                                            } catch (e) {
                                                console.log(e);
                                            }
                                        }}
                                        aria-label={item.title}
                                    >
                                        {t('products.buy_now')}
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    ))}
            </div>
        </div>
    );
};

export default Products;
